/**
 * CX-ADR-019 AppConfig-backed flag tier. Kept in a separate module because it
 * follows the ADR `{ enabled: boolean }` shape, unlike the legacy bare-boolean
 * `FeatureFlags` below.
 */
export * from "./app-config-flags.js";

/**
 * Feature flags shipped to both the webapp and the backend Lambdas. The same
 * type is read by `WebAppProps` (frontend) and `ApplicationStageProps` (backend),
 * so flag names cannot drift between sides.
 *
 * Flip semantics:
 * - `false` is the kill-switch state. The feature should be invisible in the UI
 *   AND unreachable via direct API calls when the flag is off.
 * - `true` enables the feature for the whole environment. There is no per-user
 *   targeting in this tier — that belongs to a future AppConfig / LaunchDarkly
 *   migration.
 *
 * Each entry must document: WHY the flag exists + the ticket(s) it gates +
 * the condition under which the flag can be removed entirely.
 */
export interface HomepageV2Flags {
  /**
   * Master switch for the V2 homepage shell. When `false`, V1 `StartPage`
   * renders unchanged.
   * Tickets: CX-1175, CX-1962, CX-2267, CX-2283.
   * Remove when: V2 has been live in prod for one release cycle and V1 is dropped.
   */
  enabled: boolean;
  /**
   * V2 banner carousel from `@cx-component-library/react-components`.
   * Tickets: CX-2283, CX-2978.
   * Remove when: the V2 banner has been live in prod for one release cycle
   * and `showLegacyCarousel` is dropped entirely.
   */
  showCovBannerCarousel: boolean;
  /**
   * Legacy V1 `SynchronizedCarousels` reused inside the V2 shell while the new
   * banner / trend sections were still under development.
   * Tickets: CX-2283, CX-2978.
   * Remove when: `showCovBannerCarousel` has been live in prod for one
   * release cycle.
   */
  showLegacyCarousel: boolean;
  /**
   * "Ahead of the curve, by design." trend carousel on the V2 homepage.
   * Tickets: CX-2308, CX-2310, CX-2311, CX-2312, CX-2978.
   * Remove when: trend section is permanently part of the homepage.
   */
  showTrendCarousel: boolean;
}

export interface FeatureFlags {
  homepageV2: HomepageV2Flags;
  /**
   * Upload & match flow: image upload, color extraction, list-view results.
   * Tickets: CX-1430, CX-1856, CX-2274, CX-2307, CX-2362.
   * Threaded through `DiscoverySection` / `ExploreImageFlow` /
   * `MatchedMaterialsPanel` as `enableListView`.
   */
  uploadMatch: boolean;
  /**
   * `/trend/:trendSlug` and `/trend/:trendSlug/:cardSlug` routes.
   * Tickets: CX-2308, CX-2311.
   */
  trendPages: boolean;
  /**
   * Discovery section: browse-by-image / application / effect / brand panels.
   * Tickets: CX-1591, CX-1592, CX-1593, CX-1724, CX-1725, CX-1728.
   */
  discoveryBrowse: boolean;
  /**
   * Color-tracking events (login, logout, search, download) written to the
   * `tracking-events` collection in DocumentDB via `POST /tracking/{eventType}`.
   * Tickets: CX-1130, CX-1883.
   */
  colorTracking: boolean;
  /**
   * Image color extraction Lambda exposed at `POST /color-match/extract`.
   * Tickets: CX-1904.
   */
  colorMatch: boolean;
  /**
   * Public DSM proxy: anonymous demo-only access via `GET /public-digital-samples`.
   * Tickets: CX-2191.
   */
  publicDsmProxy: boolean;
  /**
   * CDP interesting-moment reporting + lead-creation pipeline.
   * Tickets: CX-1130, CX-1742, CX-1883, CX-1884, CX-2385.
   */
  cdpLeadCreation: boolean;
  /**
   * Writes to the UserInfo DynamoDB table at registration / promotion time.
   * Reads stay always-on so the authorizer keeps working.
   * Tickets: CX-1711, CX-1883, CX-2352.
   */
  userInfoWrites: boolean;
  /**
   * V2 onboarding gate: check-whitelist → accessLevel resolution → role
   * selection modal for external users. When `false`, the frontend gate is
   * bypassed (gateState goes straight to "ready"), RoleSelectionDialog is
   * suppressed, and the check-whitelist Lambda falls back to S3-based domain
   * validation (matching pre-gate production behavior).
   * Tickets: CX-2743.
   * Remove when: onboarding V2 has been live in prod for one release cycle.
   */
  onboardingV2: boolean;
  /**
   * Circular CMF Guide: `/circular-cmf-guide` landing page and its four chapter
   * sub-pages (Reborn, Renewed & Restored, Designed to Last, Digital Twins).
   * Tickets: CX-2725, CX-2726, CX-2727, CX-2728, CX-2729.
   * Remove when: the Circular CMF Guide is permanently part of the Inspiration area.
   */
  circularCmfGuide: boolean;
  /**
   * Two-tier color sort using AxF-derived CIE Lab from DSM. When `true`,
   * `/color-search` sorts by `sortKeys.color.(tier, index)` so AxF-measured
   * chips lead each color group and ELJ-only materials follow. When `false`,
   * the legacy single-field sort on
   * `eljComputed.previewSpecification.computed.colorSortIndex` is used.
   * Only the sort order is gated — the ComputeColorSortKeys step and the
   * `axfDerivedColor` field on `colored-materials` keep populating either way,
   * so flipping the flag on is instant with no re-import needed.
   * Tickets: CX-325.
   * Remove when: two-tier sort has been live in prod for one release cycle.
   */
  twoTierColorSort: boolean;
}

/**
 * Default for environments that should ship in the same shape as today's
 * production: every new feature off. Used as the prod default at the first
 * deploy of `main` and as the in-flight loading default in the webapp so
 * gated UI never flickers on.
 */
export const ALL_FLAGS_OFF: FeatureFlags = {
  homepageV2: {
    enabled: false,
    showCovBannerCarousel: false,
    showLegacyCarousel: false,
    showTrendCarousel: false,
  },
  uploadMatch: false,
  trendPages: false,
  discoveryBrowse: false,
  colorTracking: false,
  colorMatch: false,
  publicDsmProxy: false,
  cdpLeadCreation: false,
  userInfoWrites: false,
  onboardingV2: false,
  circularCmfGuide: false,
  twoTierColorSort: false,
};

/**
 * Default for QA: every feature on so the QA environment continues to exercise
 * everything `main` has built.
 */
export const ALL_FLAGS_ON: FeatureFlags = {
  homepageV2: {
    enabled: true,
    showCovBannerCarousel: true,
    showLegacyCarousel: false,
    showTrendCarousel: true,
  },
  uploadMatch: true,
  trendPages: true,
  discoveryBrowse: true,
  colorTracking: true,
  colorMatch: true,
  publicDsmProxy: true,
  cdpLeadCreation: true,
  userInfoWrites: true,
  onboardingV2: true,
  circularCmfGuide: true,
  twoTierColorSort: true,
};
